import { useEffect, useId, useRef, useState } from 'react';
import { format, isValid, parse } from 'date-fns';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { Field, Label } from '../catalyst/fieldset';
import { Input } from '../catalyst/input';
import { Dialog, DialogBody, DialogTitle } from '../catalyst/dialog';

export default function DatePicker({
  value,
  onChange,
  label,
  disabled,
  disableDates: { before, after } = {},
}) {
  const dialogRef = useRef(null);
  const dialogId = useId();

  // Hold the month in state to control the calendar when the input changes
  const [month, setMonth] = useState(value || new Date());

  // Hold the input value in state
  const [inputValue, setInputValue] = useState(format(value || Date.now(), 'dd/MM/yyyy'));
  useEffect(() => {
    if (value && inputValue !== format(value, 'dd/MM/yyyy')) {
      setInputValue(format(value, 'dd/MM/yyyy'));
    }
  }, [value]);

  // Hold the dialog visibility in state
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Function to toggle the dialog visibility
  const toggleDialog = () => setIsDialogOpen(!isDialogOpen);

  // Hook to handle the body scroll behavior and focus trapping.
  useEffect(() => {
    const handleBodyScroll = (isOpen) => {
      document.body.style.overflow = isOpen ? 'hidden' : '';
    };
    if (!dialogRef.current) return;
    if (isDialogOpen) {
      handleBodyScroll(true);
      dialogRef.current.showModal();
    } else {
      handleBodyScroll(false);
      dialogRef.current.close();
    }
    return () => {
      handleBodyScroll(false);
    };
  }, [isDialogOpen]);

  const handleDayPickerSelect = (date) => {
    if (!date) {
      setInputValue('');
      onChange(undefined);
    } else {
      onChange(date);
      setInputValue(format(date, 'dd/MM/yyyy'));
    }
    toggleDialog();
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value); // keep the input value in sync

    const parsedDate = parse(e.target.value, 'dd/MM/yyyy', new Date());

    if (isValid(parsedDate)) {
      onChange(parsedDate);
      setMonth(parsedDate);
    } else {
      // TODO onChange(undefined);
      // console.log('invalid date', e.target.value);
    }
  };

  return (
    <Field>
      <Label htmlFor="date-input">{label}</Label>
      <Input
        style={{ fontSize: 'inherit' }}
        id="date-input"
        type="text"
        value={inputValue}
        placeholder={'dd/MM/yyyy'}
        onChange={handleInputChange}
        onClick={toggleDialog}
        onBlur={() => {
          if (!inputValue) {
            onChange(inputValue);
          }
        }}
        disabled={disabled}
      />
      <Dialog
        open={isDialogOpen}
        onClose={toggleDialog}
        id={dialogId}
        size="sm"
      >
        <DialogTitle onClose={toggleDialog}>Select date</DialogTitle>
        <DialogBody>
          <DayPicker
            month={month}
            onMonthChange={setMonth}
            autoFocus
            mode="single"
            selected={value}
            captionLayout="dropdown"
            onSelect={handleDayPickerSelect}
            locale="enGB"
            disabled={{ before, after }}
            weekStartsOn={1}
            startMonth={new Date(2000, 0)}
            endMonth={new Date(2050, 11)}
          />
        </DialogBody>
      </Dialog>
    </Field>
  );
}
